import { useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, KeyRound, Loader2, LogOut, Moon, Plus, Sun } from "lucide-react";
import { api, ApiError, type Scope, type User } from "../lib/api";
import { ago, CopyText, SCOPE_HELP, SCOPE_WHO } from "./IntegrationSettings";

const input = "rounded-md border border-line bg-panel px-2 py-1 text-[12.5px] disabled:opacity-70";
const card = "rounded-xl border border-line bg-panel p-4";
const btn = "flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3 disabled:opacity-50";

/**
 * The whole app, as a vendor sees it: who they are, the API documentation, the scopes
 * the airport allowed them, and the keys they issued for themselves. No board and no
 * way to one; the server refuses everything else, this page simply does not ask.
 */
export function VendorPage({ user, dark, setDark }: { user: User; dark: boolean; setDark: (v: boolean) => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-keys"], queryFn: api.myKeys });
  const [keyName, setKeyName] = useState(user.name);
  const [scopes, setScopes] = useState<Scope[]>(() => user.apiScopes.filter((s) => s === "read"));
  const [newKey, setNewKey] = useState<{ label: string; value: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const d = q.data;
  const allowed = d?.scopes ?? user.apiScopes;
  const refresh = () => qc.invalidateQueries({ queryKey: ["my-keys"] });
  const base = d?.publicUrl || location.origin;

  return (
    <div className="flex h-full flex-col bg-bg">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-panel px-3 sm:px-4">
        <div className="grid size-8 place-items-center rounded-lg bg-brand text-brand-ink">
          <svg viewBox="0 0 32 32" className="size-5">
            <path d="M8 20h16M11 14h10M14 8h4" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[14px] font-bold tracking-tight">Vectro Allocate API</div>
          <div className="text-[10.5px] text-ink-3">Developer access</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button className="rounded-lg p-2 text-ink-2 hover:bg-panel-2" onClick={() => setDark(!dark)} aria-label="Toggle dark mode">
            {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
          <div className="text-right leading-tight">
            <div className="text-[12px] font-semibold">{user.name}</div>
            <div className="text-[10px] text-ink-3">vendor</div>
          </div>
          <button
            className="rounded-lg p-2 text-ink-2 hover:bg-panel-2"
            onClick={async () => {
              await api.logout();
              await qc.invalidateQueries();
            }}
            aria-label="Sign out"
          >
            <LogOut className="size-4" />
          </button>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl space-y-3 p-4">
          <div className={card}>
            <div className="text-[18px] font-bold">Welcome, {user.name}</div>
            <p className="mt-1 text-[12.5px] text-ink-2">
              This account is for your integration with Vectro Allocate. It reads the API documentation and manages your own keys; it has no access to the
              airport's board, plans or settings. Each key you create may carry the scopes listed below, and nothing beyond them. The key is shown once, when it is
              made; after that only its first characters are kept.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px]">
              <span className="font-semibold">Your allowed scopes:</span>
              {allowed.map((s) => (
                <span key={s} className="rounded bg-panel-2 px-1.5 py-0.5 text-[11.5px]" title={SCOPE_HELP[s] ?? s}>
                  <b>{s}</b>
                  {SCOPE_WHO[s] ? ` (${SCOPE_WHO[s]})` : ""}
                </span>
              ))}
              {allowed.length === 0 && <span className="text-ink-3">none yet; ask the airport's administrator</span>}
            </div>
            <div className="mt-2 text-[12px] text-ink-3">
              Send requests to <CopyText text={`${base}/api/v1`} /> with <span className="font-mono">Authorization: Bearer yb_…</span>
            </div>
          </div>

          {newKey && (
            <div className="rounded-xl border-2 border-warn bg-warn/5 p-4">
              <div className="text-[13px] font-bold">{newKey.label}: copy it now, it will not be shown again.</div>
              <div className="mt-2">
                <CopyText text={newKey.value} />
              </div>
              <button className={clsx(btn, "mt-2")} onClick={() => setNewKey(null)}>
                I've saved it
              </button>
            </div>
          )}

          <div className={card}>
            <div className="mb-2 flex items-center gap-2 text-[14px] font-bold">
              <KeyRound className="size-4" /> Your API keys
            </div>
            <div className="divide-y divide-line">
              {d?.keys.map((k) => (
                <div key={k.id} className={clsx("flex flex-wrap items-center gap-3 py-2 text-[12.5px]", k.revokedAt && "opacity-50")}>
                  <span className="w-40 font-semibold">{k.name}</span>
                  <span className="font-mono text-[11.5px] text-ink-3">{k.prefix}…</span>
                  <span className="flex gap-1">
                    {k.scopes.map((s) => (
                      <span key={s} className="rounded bg-panel-2 px-1.5 text-[11px]">
                        {s}
                      </span>
                    ))}
                  </span>
                  <span className="flex-1 text-[11.5px] text-ink-3">
                    created {DateTime.fromISO(k.createdAt).toFormat("d LLL yyyy")} · {k.revokedAt ? "revoked" : `last used ${k.lastUsedAt ? ago(Date.parse(k.lastUsedAt)) : "never"}`}
                  </span>
                  {!k.revokedAt && (
                    <button
                      className="text-[12px] font-semibold text-bad hover:underline"
                      onClick={async () => {
                        if (!confirm(`Revoke the key "${k.name}"? Anything using it stops at once.`)) return;
                        try {
                          await api.revokeMyKey(k.id);
                          await refresh();
                        } catch (err) {
                          setError(err instanceof ApiError ? err.message : "Could not revoke that key");
                        }
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
              {q.isLoading && <Loader2 className="my-2 size-4 animate-spin text-ink-3" />}
              {d && !d.keys.length && <div className="py-2 text-[12px] text-ink-3">No keys yet. Create one below.</div>}
            </div>

            <div className="mt-3 text-[12px] font-semibold text-ink-3">Create a key</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <input className={clsx(input, "w-44")} value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="What will use it" aria-label="Key name" />
              {allowed.map((s) => (
                <label key={s} className="flex items-center gap-1 text-[12px]" title={SCOPE_HELP[s] ?? s}>
                  <input type="checkbox" checked={scopes.includes(s)} onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))} />
                  <span>
                    <b>{s}</b>
                    {SCOPE_WHO[s] ? ` (${SCOPE_WHO[s]})` : ""}
                  </span>
                </label>
              ))}
              <button
                className={btn}
                disabled={busy || !keyName.trim() || scopes.length === 0}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    const r = await api.createMyKey(keyName.trim(), scopes);
                    setNewKey({ label: `API key for ${r.row.name}`, value: r.key });
                    await refresh();
                  } catch (err) {
                    setError(err instanceof ApiError ? err.message : "Could not create the key");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <Plus className="size-4" /> Create key
              </button>
            </div>
            {error && <div className="mt-2 text-[12.5px] text-bad">{error}</div>}
          </div>

          <div className={clsx(card, "flex flex-wrap items-center gap-3")}>
            <BookOpen className="size-5 text-info" />
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-bold">API documentation (v{d?.apiVersion ?? "1"})</div>
              <div className="text-[12px] text-ink-3">Every endpoint, the webhook payload and the Apron contract. Try a call from the page with one of your keys.</div>
            </div>
            <a href="/api/docs" target="_blank" rel="noreferrer" className={btn}>
              Open docs in a new tab
            </a>
            <a href="/api/v1/openapi.json" target="_blank" rel="noreferrer" className={btn}>
              OpenAPI JSON
            </a>
          </div>
          <iframe src="/api/docs" title="API documentation" className="h-[70vh] w-full rounded-xl border border-line bg-white" />
        </div>
      </main>
    </div>
  );
}
